/**
 * LibrarySidebar Component
 * Left sidebar for playlist/catalog library navigation with tabs and search
 */

import React, { useState, useCallback } from 'react';
import { Library, Search, Plus, RefreshCw, Download, ListOrdered } from 'lucide-react';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { PlaylistList } from '../library/PlaylistList';
import { CrateBrowser } from '../library/CrateBrowser';
import { ResizeHandle } from './ResizeHandle';
import { useWorkbenchStore } from '../../stores/workbench';
import { useCreatePlaylist, useAddTracks, playlistKeys } from '../../api/hooks';
import { importPlaylistFromClipboard } from '../../utils/spotify-import';

const MIN_WIDTH = 256;  // 16rem
const MAX_WIDTH = 640;  // 40rem
const DEFAULT_WIDTH = 320; // 20rem
const WIDE_THRESHOLD = 384; // 24rem (switches to wide mode)
const STORAGE_KEY = 'splister:librarySidebarWidth';
const SORT_STORAGE_KEY = 'splister:playlistSortOrder';

type TabType = 'playlists' | 'crates';
type SortOrder = 'name-asc' | 'name-desc' | 'recent';

export function LibrarySidebar() {
  const [activeTab, setActiveTab] = useState<TabType>('playlists');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    return (stored as SortOrder) || 'recent';
  });
  const [width, setWidth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Math.min(Math.max(Number(stored), MIN_WIDTH), MAX_WIDTH) : DEFAULT_WIDTH;
  });
  const setActivePlaylist = useWorkbenchStore((state) => state.setActivePlaylist);
  const markDirty = useWorkbenchStore((state) => state.markDirty);
  const createPlaylist = useCreatePlaylist();
  const addTracks = useAddTracks();
  const queryClient = useQueryClient();
  const isFetchingPlaylists = useIsFetching({ queryKey: playlistKeys.lists() });

  const isWideMode = width >= WIDE_THRESHOLD;

  const handleResize = useCallback((newWidth: number) => {
    setWidth(newWidth);
    localStorage.setItem(STORAGE_KEY, String(newWidth));
  }, []);

  // Debounce search input (300ms)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleNewProject = useCallback(async () => {
    // Check for unsaved changes before creating new playlist
    const isDirty = useWorkbenchStore.getState().isDirty;

    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes in the current playlist. Creating a new playlist will discard these changes.\n\nDo you want to continue?'
      );
      if (!confirmed) {
        return; // User cancelled
      }
    }

    try {
      // Create a new Spotify playlist
      const createdPlaylist = await createPlaylist.mutateAsync({
        name: 'New Playlist',
        description: '',
        public: false,
      });

      // Convert created playlist to PlaylistDetail format and load into workbench
      const playlistDetail = {
        id: createdPlaylist.id,
        name: createdPlaylist.name,
        description: '',
        public: false,
        collaborative: false,
        owner: {
          id: 'current-user',
          displayName: 'You',
          uri: 'spotify:user:current-user',
        },
        snapshotId: '',
        uri: createdPlaylist.uri,
        images: [],
        tracks: {
          total: 0,
          items: [],
        },
      };

      setActivePlaylist(playlistDetail);

      // Mark as dirty so "Save Changes" bar appears
      // This allows user to rename and save in one flow
      markDirty();
    } catch (error) {
      console.error('Failed to create playlist:', error);
      // TODO: Show user-friendly error notification
    }
  }, [createPlaylist, setActivePlaylist, markDirty]);

  const handleDownloadFromClipboard = useCallback(async () => {
    try {
      await importPlaylistFromClipboard({
        queryClient,
        createPlaylist: (data) => createPlaylist.mutateAsync(data),
        addTracks: (params) => addTracks.mutateAsync(params),
        setActivePlaylist,
        isDirty: useWorkbenchStore.getState().isDirty,
      });
    } catch (error) {
      console.error('Failed to import playlist from clipboard:', error);
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert('Failed to import playlist. Please try again.');
      }
    }
  }, [createPlaylist, addTracks, setActivePlaylist, queryClient]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: playlistKeys.all });
  }, [queryClient]);

  const handleToggleSort = useCallback(() => {
    setSortOrder(prev => {
      const next = prev === 'recent' ? 'name-asc' : prev === 'name-asc' ? 'name-desc' : 'recent';
      localStorage.setItem(SORT_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const getSortIcon = () => {
    if (sortOrder === 'name-asc') return <span className="leading-none">↑ A-Z</span>;
    if (sortOrder === 'name-desc') return <span className="leading-none">↓ Z-A</span>;
    return <ListOrdered className="w-4 h-4" />; // Recent - original order
  };

  const getSortTitle = () => {
    if (sortOrder === 'name-asc') return 'Sort: A to Z (click for Z to A)';
    if (sortOrder === 'name-desc') return 'Sort: Z to A (click for Recent)';
    return 'Sort: Most Recent (click for A to Z)';
  };

  return (
    <aside
      className="relative border-r border-zinc-800 bg-zinc-900 flex flex-col"
      style={{ width: `${width}px` }}
    >
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Library className="w-5 h-5 text-emerald-500" />
            <h2 className="text-base font-semibold text-zinc-100">Library</h2>
          </div>

          {/* Action buttons group */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleToggleSort}
              className="p-1.5 rounded-md text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 transition-all text-[10px] font-medium flex items-center justify-center"
              title={getSortTitle()}
            >
              {getSortIcon()}
            </button>

            <button
              onClick={handleDownloadFromClipboard}
              className="p-1.5 rounded-md text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 transition-all"
              title="Import playlist from clipboard URL"
            >
              <Download className="w-4 h-4" />
            </button>

            <button
              onClick={handleNewProject}
              className="p-1.5 rounded-md text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 transition-all"
              title="Create new empty playlist"
            >
              <Plus className="w-4 h-4" />
            </button>

            <button
              onClick={handleRefresh}
              disabled={isFetchingPlaylists > 0}
              className="p-1.5 rounded-md text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh playlists"
            >
              <RefreshCw className={`w-4 h-4 ${isFetchingPlaylists > 0 ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="p-4 border-b border-zinc-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchQuery}
            onChange={handleSearchChange}
            className="
              w-full pl-9 pr-3 py-2
              bg-zinc-800/50 border border-zinc-700
              rounded-lg text-xs text-zinc-100 placeholder-zinc-500
              focus:outline-none focus:ring-2 focus:ring-emerald-500/50
              transition-all
            "
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                text-zinc-500 hover:text-zinc-300
                transition-colors
              "
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800">
        <button
          onClick={() => setActiveTab('playlists')}
          className={`
            flex-1 px-4 py-3 text-xs font-medium
            transition-all border-b-2
            ${activeTab === 'playlists'
              ? 'text-emerald-400 border-emerald-500'
              : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }
          `}
        >
          Playlists
        </button>
        <button
          onClick={() => setActiveTab('crates')}
          className={`
            flex-1 px-4 py-3 text-xs font-medium
            transition-all border-b-2
            ${activeTab === 'crates'
              ? 'text-emerald-400 border-emerald-500'
              : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }
          `}
        >
          Crates
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {activeTab === 'playlists' ? (
          <PlaylistList searchQuery={debouncedSearch} isWideMode={isWideMode} sortOrder={sortOrder} />
        ) : (
          <CrateBrowser searchQuery={debouncedSearch} />
        )}
      </div>

      {/* Resize handle */}
      <ResizeHandle
        onResize={handleResize}
        minWidth={MIN_WIDTH}
        maxWidth={MAX_WIDTH}
      />
    </aside>
  );
}
